<template>
  <SrpModal v-model:isVisible="isModalVisible" size="large" :isClosable="false" :isCloseButtonVisible="!isSaving">
    <template #content>
      <SelectCreatorStep v-show="currentStep === WizardSteps.SelectCreator" ref="domRefSelectCreatorStep" />
      <WhatKindOfWorkStep v-show="currentStep === WizardSteps.WhatKindOfWork" ref="domRefWhatKindOfWorkStep" @selectTemplate="validateAndGoToNextStep" />
      <StartConversationStep v-show="currentStep === WizardSteps.StartConversation" ref="domRefStartConversationStep" :isSaving="isSaving" />
    </template>

    <template #footer>
      <SrpButton v-if="currentStep === 1" color="gray" fill="outlined" style="margin-right: 12px" @click="isModalVisible = false" :isDisabled="isSaving"> Cancel </SrpButton>
      <SrpButton v-else color="gray" fill="outlined" style="margin-right: 12px" @click="currentStep -= 1" :isDisabled="isSaving"> Back </SrpButton>

      <SrpButton v-if="[WizardSteps.SelectCreator, WizardSteps.WhatKindOfWork].includes(currentStep)" @click="validateAndGoToNextStep">
        Next
        <template #iconRight><IconEmbedded name="arrow-right_3" :size="19" /></template>
      </SrpButton>

      <SrpButton v-else @click="validateAndGoToNextStep" :isDisabled="isSaving">
        <template #icon>
          <LoadingIcon v-if="isSaving" :size="22" />
          <IconEmbedded v-else name="send_2" :size="22" />
        </template>
        Initiate Collab and Send Message
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import CustomerCustomCollabsRepo from "@repos/CustomerCustomCollabsRepo";
import CustomerSavedCreatorsRepo from "@repos/CustomerSavedCreatorsRepo";
import { useRoute, useRouter } from "vue-router";
import { MetricSender } from "@/helpers/MetricSender";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
// steps
import SelectCreatorStep from "./SelectCreatorStep.vue";
import WhatKindOfWorkStep from "./WhatKindOfWorkStep.vue";
import StartConversationStep from "./StartConversationStep.vue";

// Types
import { NewCustomCollabPayload } from "@contracts/customCollabs";

// Router
const router = useRouter();

// Stores
import { useNewCustomCollabWizardStore } from "@stores/newCustomCollabWizardStore";
const newCustomCollabWizardStore = useNewCustomCollabWizardStore();

// DOM refs
const domRefSelectCreatorStep = ref<any>(null);
const domRefWhatKindOfWorkStep = ref<any>(null);
const domRefStartConversationStep = ref<any>(null);

// Toggle modal ===============================================================
const isModalVisible = ref<boolean>(false);

// Change steps ===============================================================
enum WizardSteps {
  SelectCreator = 1,
  WhatKindOfWork = 2,
  StartConversation = 3,
}
const currentStep = ref<number>(WizardSteps.SelectCreator);

// Define props
const props = defineProps<{
  customerId: string;
}>();

// Load creators ==============================================================
onMounted(async () => {
  newCustomCollabWizardStore.isLoadingCreators = true;
  newCustomCollabWizardStore.savedCreatorsPayload = await CustomerSavedCreatorsRepo.loadAllData(props.customerId);
  newCustomCollabWizardStore.isLoadingCreators = false;

  MetricSender.sendMetric("Cust-NewCustomCollabWizard", null);
});

// Load templates =============================================================
onMounted(async () => {
  newCustomCollabWizardStore.isLoadingTemplates = true;
  newCustomCollabWizardStore.customCollabTemplatesList = await CustomerCustomCollabsRepo.getTemplates(props.customerId);
  newCustomCollabWizardStore.isLoadingTemplates = false;
});

// Validate and go to next step ===============================================
function validateAndGoToNextStep() {
  if (currentStep.value === WizardSteps.SelectCreator && !newCustomCollabWizardStore.isSelectCreatorFormValid) {
    domRefSelectCreatorStep.value.showValidationErrorNotes();
    return;
  } else if (currentStep.value === WizardSteps.WhatKindOfWork && !newCustomCollabWizardStore.selectedTemplateId) {
    domRefWhatKindOfWorkStep.value.showValidationErrorNotes();
    return;
  } else if (currentStep.value === WizardSteps.StartConversation) {
    if (newCustomCollabWizardStore.isInitMessageIncludesTemplatePlaceholders) {
      domRefStartConversationStep.value.showTemplatePlaceholdersError();
      return;
    } else if (newCustomCollabWizardStore.initialMessage?.length === 0) {
      domRefStartConversationStep.value.showEmptyInitMsgError();
      return;
    } else {
      initCollabAndSendMessage();
      return;
    }
  }

  currentStep.value += 1;
}

// Init collab and send message ===============================================
const isSaving = ref<boolean>(false);

async function initCollabAndSendMessage() {
  const payload: NewCustomCollabPayload = {
    initialMessage: newCustomCollabWizardStore.initialMessage,
    templateId: newCustomCollabWizardStore.selectedTemplateId,
  };

  if (newCustomCollabWizardStore.selectedCreatorId) {
    payload.creatorId = newCustomCollabWizardStore.selectedCreatorId;
  } else if (newCustomCollabWizardStore.invitedCreatorEmail) {
    payload.invitedCreatorEmail = newCustomCollabWizardStore.invitedCreatorEmail;
  }

  isSaving.value = true;
  const newCollab = await CustomerCustomCollabsRepo.createCustomCollab(props.customerId, payload);
  isSaving.value = false;
  MetricSender.sendMetric("Cust-CreatedCustomCollab", null);

  await router.push({ name: "CustomerCustomCollabDetails", params: { pageId: newCollab.customerId, customCollabId: newCollab.id } });

  clearFormData();
}

function clearFormData() {
  newCustomCollabWizardStore.selectedCreatorId = "";
  newCustomCollabWizardStore.invitedCreatorEmail = "";

  newCustomCollabWizardStore.selectedTemplateId = "";

  newCustomCollabWizardStore.initialMessage = "";
}

// Open modal function for external use =======================================
async function openModal(selectedCreatorId = "") {
  isModalVisible.value = true;

  if (selectedCreatorId) {
    await nextTick();
    domRefSelectCreatorStep.value.setSelectedCreator(selectedCreatorId);
  }
}

// Define expose ==============================================================
defineExpose({
  openModal,
  closeModal: () => (isModalVisible.value = false),
});
</script>

<style scoped lang="scss"></style>
